import React from "react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import {
  StatsBand,
  WhyMatters,
  CardGrid,
  Methodology,
  TechStack,
  Audience,
  Resources,
  Eyebrow,
} from "@/components/ServiceSections";
import { Certifications, TrustCommitments } from "@/components/TechSections";
import { IMAGES, METHODOLOGY_GENERIC, CERTIFICATIONS, TRUST_COMMITMENTS } from "@/content/services";

const ABOUT_STATS = [
  { value: "100%", label: "Cybersecurity-only delivery, no generic IT" },
  { value: "24/7", label: "Security operations coverage available" },
  { value: "7", label: "Compliance frameworks mapped on every engagement" },
  { value: "Zero", label: "Findings reported without human verification" },
];

const PRACTICES = [
  {
    icon: "Crosshair",
    title: "Offensive Security",
    copy: "Penetration testers and red-teamers who think like adversaries and document like auditors. Every critical finding is manually exploited and proven, never inferred from a scanner.",
  },
  {
    icon: "Radar",
    title: "SOC & Detection Engineering",
    copy: "Analysts and detection engineers who run monitoring, threat hunting and incident response, and who write the detection content behind it rather than shipping vendor defaults.",
  },
  {
    icon: "Boxes",
    title: "Cloud & Security Architecture",
    copy: "Engineers who design and deploy landing zones, SIEM and XDR platforms for regulated environments, with policy-as-code guardrails and infrastructure that is reviewable.",
  },
  {
    icon: "Scale",
    title: "GRC & Compliance",
    copy: "Consultants who translate frameworks into controls and controls into evidence, so an audit validates your posture instead of exposing it.",
  },
];

const PRINCIPLES = [
  {
    number: "01",
    title: "One discipline, done properly",
    copy: "Lanvaus is exclusively a cybersecurity services company. We don't resell hardware and we don't do general IT. Our entire organization exists to find weaknesses, stop attacks and keep you defensible.",
  },
  {
    number: "02",
    title: "Engineers before salespeople",
    copy: "The people who scope your engagement are the people who deliver it. No handoff to a junior bench, no findings you can't get an engineer on a call to explain.",
  },
  {
    number: "03",
    title: "Evidence over adjectives",
    copy: "Every claim we make about your environment comes with proof: reproduction steps, logs, screenshots, control mappings. If we can't evidence it, we don't report it.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        breadcrumb={["Home", "About"]}
        pill="About Lanvaus"
        titleLines={[
          [{ t: "Engineer-led." }],
          [{ t: "Compliance-aligned.", accent: true }],
        ]}
        description="Lanvaus is a security-first cybersecurity company serving regulated industries and growing organizations. We test, build, and run the security controls our clients depend on, and we map every one of them to the frameworks they are audited against."
        primaryCta={{ label: "Get a Consultation", to: "/contact" }}
        secondaryCta={{ label: "Explore Our Services", to: "/services" }}
        image={IMAGES.serverRoom}
        imageAlt="Dark data centre server racks protected by Lanvaus security controls"
        showCompliance={false}
      />

      <StatsBand stats={ABOUT_STATS} testId="about-stats-band" />

      <section className="relative overflow-hidden border-b border-white/10" data-testid="about-who-we-are">
        <div className="glow-orb -right-32 top-1/2 h-[420px] w-[420px] -translate-y-1/2 bg-[#4C5A8C]/20" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[1.35fr_1fr] lg:gap-16 lg:px-16">
          <div className="relative">
            <Reveal>
              <Eyebrow>Who We Are</Eyebrow>
              <h2 className="mt-5 font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.5rem] lg:leading-[1.14]">
                Two words describe how we work, <span className="text-[#8E9AEA]">and we hold ourselves to both.</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#DDE3F2]">
                <span className="font-semibold text-white">Engineer-led</span>: the people who scope your engagement
                are the ones who run it. No handover to a delivery team that was not in the room.{" "}
                <span className="font-semibold text-white">Compliance-aligned</span>: every finding, detection and
                design decision is mapped to the framework you are audited against, so security work doubles as audit
                evidence instead of creating a second pile of it.
              </p>
              <p className="mt-5 text-base leading-relaxed text-[#DDE3F2]">
                In practice that means four things under one roof: penetration testing that proves what an attacker
                could reach, managed security operations that watch for it around the clock, cloud and SIEM
                architecture built to your regulatory obligations, and an open-source SOC engineered for Indian
                businesses that carry enterprise obligations on a smaller budget.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.12}>
            <div
              className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#121A2E] via-[#0D1322] to-[#0B0F1A] p-6 sm:p-8"
              data-testid="about-shield-card"
            >
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(142,154,234,0.16),transparent_65%)]"
                aria-hidden
              />
              <img
                src="/assets/lanvaus-shield.png"
                alt="Lanvaus shield emblem representing layered security"
                width="785"
                height="880"
                loading="lazy"
                decoding="async"
                className="relative mx-auto h-[220px] w-auto object-contain sm:h-[280px] lg:h-auto lg:max-h-[360px] lg:w-full"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <WhyMatters
        why={{
          eyebrow: "Our Mission",
          title: { pre: "Make enterprise-grade defense ", accent: "reachable." },
          paragraphs: [
            "Security maturity has become a function of budget. Large enterprises buy platforms, staff a SOC and pass their audits. Everyone else is left with a compliance obligation, a spreadsheet and hope. That gap is where most breaches now happen.",
            "We exist to close it. The same engineers who build landing zones and detection content for regulated enterprises also built our Open Source SOC product for Indian SMBs, because the regulations don't scale down with headcount and neither should the defense.",
          ],
          image: IMAGES.serverRoom,
        }}
      />

      <section className="border-y border-white/10 bg-[#0E1526]" data-testid="principles-section">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow>How We Operate</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem] lg:leading-[1.12]">
              Three principles we <span className="text-[#8E9AEA]">don't negotiate on.</span>
            </h2>
          </Reveal>
          <div className="mt-10 sm:mt-14 grid gap-8 md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.number} delay={i * 0.1}>
                <div
                  className="h-full rounded-xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 transition-colors duration-300 hover:border-[#8E9AEA]/35"
                  data-testid={`principle-${p.number}`}
                >
                  <span className="font-display text-sm font-bold tracking-[0.3em] text-[#8E9AEA]">{p.number}</span>
                  <h3 className="mt-5 font-display text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#DDE3F2]/90">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="practices-section">
        <Reveal className="text-center">
          <Eyebrow>Team &amp; Expertise</Eyebrow>
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-5xl lg:leading-[1.1]">
            Four practices. One integrated <span className="text-[#8E9AEA]">defense team.</span>
          </h2>
        </Reveal>
        <div className="mt-12 sm:mt-16">
          <CardGrid cards={PRACTICES} cols="md:grid-cols-2 lg:grid-cols-4" testPrefix="team-card" />
        </div>
      </section>

      <Methodology
        eyebrow="Our Methodology"
        title={{ pre: "One disciplined method, ", accent: "every engagement." }}
        copy="Penetration test, managed SOC or a full security architecture: the phases are the same, the artifacts are the same, and you always know which phase you're in and what comes out of it."
        steps={METHODOLOGY_GENERIC}
        showButton={false}
        testId="about-methodology"
      />

      <Audience
        eyebrow="Who We Work With"
        testId="about-audience"
        audience={{
          title: { pre: "The people who call us ", accent: "usually own the risk." },
          copy: "Our engagements are sponsored by the people accountable when something goes wrong: security, engineering, compliance and the executives who sign the attestation.",
          cards: [
            { title: "CISO / Security Lead", note: "Independent validation and 24/7 coverage" },
            { title: "CTO / VP Engineering", note: "Secure releases without slowing delivery" },
            { title: "Compliance & Risk", note: "Evidence auditors and regulators accept" },
            { title: "IT Leadership", note: "Alert fatigue offloaded to engineers" },
            { title: "Founders & CEOs", note: "Enterprise posture on an SMB budget" },
            { title: "Boards & Risk Committees", note: "Measurable detection and response" },
          ],
        }}
      />

      <Certifications certifications={CERTIFICATIONS} />

      <TrustCommitments
        items={TRUST_COMMITMENTS}
        title={{ pre: "The questions buyers ask us ", accent: "before they trust us." }}
      />

      <TechStack
        stack={{
          title: { pre: "Frameworks we are ", accent: "measured against." },
          copy: "Every deliverable is mapped to the frameworks your auditors, customers and regulators actually ask about, so security work doubles as audit evidence.",
          badges: [
            "SOC 2 Type II",
            "ISO 27001",
            "HIPAA",
            "PCI-DSS",
            "NIST 800-53",
            "CMMC 2.0",
            "DPDP Act 2023",
            "CERT-In Directions",
          ],
        }}
      />

      <Resources resources={[]} pagePath="/about" />

      <CTABand
        title={{ pre: "Meet the team behind ", accent: "the defense." }}
        copy="Start with a consultation. We'll map your current posture, your compliance targets and the shortest defensible path between them."
        primary={{ label: "Get a Consultation", to: "/contact" }}
        secondary={{ label: "View All Services", to: "/services" }}
      />
    </div>
  );
}
